// src/app/services/auth.service.ts
import { Injectable, signal, effect } from '@angular/core';
import { Web2ServiceService } from './web2-service.service';
import { STORAGE_KEYS } from '../common/constants/common.constants';
import { Web3ServiceService } from './web3-service.service';
import { Router } from '@angular/router';
import { WalletService } from './wallet.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderService } from './loader.service';
import { ToastService } from './toast.service';
import { AppKitService } from './app-kit.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

  isLoggedIn = signal(false);   // Angular 16–19 signal
  private isConnected: Subject<boolean> = new Subject();
  operation: Subject<string> = new Subject();
  isConnected$: Observable<boolean> = this.isConnected.asObservable();
  private userInfo: any = null;
  public isInitialProfileLoaded: boolean = false;

  // Global Portfolio Stream
  private portfolioSummarySource = new BehaviorSubject<any>(null);
  portfolioSummary$ = this.portfolioSummarySource.asObservable();
  private portfolioStreamSubscription: any = null;

  constructor(
    private router: Router,
    private api: ApiService,
    private wallet: WalletService,
    private service: Web2ServiceService,
    private web3service: Web3ServiceService,
    private loader: LoaderService,
    private toastr: ToastService,
    private appKitService: AppKitService
  ) {
    if (this.getSessionToken()) {
      this.isLoggedIn.set(true);
      this.startPortfolioStream();
    }

    // Monitor for account switching in wallet
    effect(() => {
      const state = this.appKitService.getCurrentWalletState();
      const userData = this.getUserData();

      const connectedAddress = state?.address;
      const loggedInAddress = userData?.public_address || userData?.publicAddress;

      // If user is logged in and we have a connected wallet address to check against
      if (this.isLoggedIn() && connectedAddress && loggedInAddress) {
        if (connectedAddress.toLowerCase() !== loggedInAddress.toLowerCase()) {
          const name = userData?.username || userData?.profile_display_name || "your logged-in account";
          this.toastr.error(`Account mismatch detected. Logging out to maintain session integrity for ${name}.`);
          this.clearSession(); // Force full logout from the application
        }
      }
    });

    // Listen for cross-tab storage changes (like email verification from another tab)
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEYS.USER_META_INFO) {
        if (event.newValue) {
          this.userInfo = JSON.parse(event.newValue);
          this.isConnected.next(true); // Notify observers
        }
      }
    });
  }

  startPortfolioStream() {
    const userData = this.getUserData();
    const userId = userData?.id;
    if (!userId) return;

    // If already streaming, don't restart unless necessary 
    // (This prevents the 'canceled' duplicate requests you saw)
    if (this.portfolioStreamSubscription) {
      return;
    }

    this.portfolioStreamSubscription = this.api.streamPortfolioSummary(userId).subscribe({
      next: (res: any) => {
        // console.log("Global Portfolio Stream Update:", res);
        this.portfolioSummarySource.next(res);
      },
      error: (err) => {
        console.error("Global Portfolio Stream Error:", err);
      }
    });
  }

  stopPortfolioStream() {
    if (this.portfolioStreamSubscription) {
      this.portfolioStreamSubscription.unsubscribe();
      this.portfolioStreamSubscription = null;
    }
    this.portfolioSummarySource.next(null);
  }

  setLoginOption(option: any): string | null {
    option = typeof option != "string" ? JSON.stringify(option) : option;
    localStorage.setItem(STORAGE_KEYS.LOGIN_TYPE, option);
    return this.getLoginOption();
  }

  getLoginOption(): any | null {
    let option: string | null = localStorage.getItem(STORAGE_KEYS.LOGIN_TYPE);
    return option ? JSON.parse(option) : option;
  }

  setUserData(userData: any): void {
    userData = this.normalizeUserData(userData);
    let profile_display_name: string = "";
    let wallet_address: string = userData.proxy_wallet || "";
    if (wallet_address)
      profile_display_name = wallet_address.slice(0, 4) + "*********" + wallet_address.slice(-4);

    userData.profile_display_name = profile_display_name;

    this.userInfo = userData;
    localStorage.setItem(STORAGE_KEYS.USER_META_INFO, JSON.stringify(userData));
    setTimeout(() => {
      this.reUpdateProfile(true); // Force stream restart to pick up new user ID
    }, 100)
  }

  private normalizeUserData(userData: any): any {
    if (!userData) {
      return userData;
    }

    const normalizedUserData = { ...userData };

    if (normalizedUserData.user_name) {
      normalizedUserData.username = normalizedUserData.user_name;
    }
    
    // No longer forcing pending_email to null here. 
    // We let the UI handle the state based on what the server actually returns.
    
    return normalizedUserData;
  }

  reUpdateProfile(restartStream: boolean = false): void {
    this.isConnected.next(true);
    if (restartStream) {
      this.stopPortfolioStream(); // Force close old stream to get new initial balance info
      this.startPortfolioStream();
    }
  }

  getUserData(): any {
    if (this.userInfo)
      return this.userInfo;

    let userData: any = localStorage.getItem(STORAGE_KEYS.USER_META_INFO);
    return userData ? JSON.parse(userData) : userData;
  }

  login(token: string) {
    this.setSessionToken(token);
    this.isLoggedIn.set(true);
    this.startPortfolioStream();
  }

  setSessionToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  getSessionToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  removeSessionToken(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setTempSessionTOken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TEMP_ACCESS_TOKEN, token);
  }

  getTempSessionToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TEMP_ACCESS_TOKEN);
  }

  removeTempSessionToken(): void {
    localStorage.removeItem(STORAGE_KEYS.TEMP_ACCESS_TOKEN)
  }

  logout() {
    this.loader.show();
    this.api
      .logout()
      .subscribe({
        next: async (result: any) => {
          await this.completeLogout();
        },
        error: async (error: HttpErrorResponse) => {
          await this.completeLogout();
        }
      });
  }

  private async completeLogout(): Promise<void> {
    await this.clearSession();
    this.loader.hide();
    this.toastr.success("Logged out successfully");
  }

  async clearSession() {
    this.stopPortfolioStream();
    this.web3service.clearState(); // Reset Web3 provider state
    let details: any = this.getLoginOption();
    if (details.type == 'web3') {
      await this.wallet.disconnect();
      localStorage.removeItem("wagmi.store");
      localStorage.removeItem("wagmi.cache");
    }
    else
      await this.service.logout();

    this.isLoggedIn.set(false);
    this.isConnected.next(false);
    this.isInitialProfileLoaded = false;
    this.userInfo = null; // Clear in-memory user data
    localStorage.removeItem(STORAGE_KEYS.LOGIN_TYPE);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_META_INFO);
    this.backToHome();
  }

  backToHome(): void {
    this.router.navigate(["/"])
  }

  isTradeEnabled(): boolean {
    let userInfo: any = this.getUserData();
    return userInfo && userInfo["enable_trading"] === true ? true : false;
  }

  sendOperationSignal(operationName: string): void {
    if (operationName === 'login') {
      // Save the current page URL so we can redirect back after login
      const returnUrl = window.location.pathname + window.location.search;
      if (returnUrl && returnUrl !== '/') {
        sessionStorage.setItem('loginReturnUrl', returnUrl);
      }
    }
    this.operation.next(operationName);
  }

  checkTokenExpired(token: string): boolean {
    try {
      let current = Date.now();
      let metadata = JSON.parse(window.atob(token.split(".")[1])).exp * 1000;
      return metadata - current <= 0;
    }
    catch (e) {
      return false;
    }
  }

  /**
   * Store email verification context so another pending email in the same
   * browser does not accidentally consume a generic "verified" flag.
   */
  setEmailVerifiedLocally(context?: { userId?: string | number | null; email?: string | null }): void {
    localStorage.setItem(STORAGE_KEYS.EMAIL_VERIFIED_LOCALLY, JSON.stringify({
      userId: context?.userId ?? null,
      email: context?.email ?? null
    }));
  }

  /**
   * Checks if email verification was completed for this exact user/email.
   * Returns true only once, then clears the flag.
   */
  checkEmailVerifiedLocally(context?: { userId?: string | number | null; email?: string | null }): boolean {
    const storedValue = localStorage.getItem(STORAGE_KEYS.EMAIL_VERIFIED_LOCALLY);
    if (!storedValue) {
      return false;
    }

    try {
      const verifiedContext = JSON.parse(storedValue);
      const userMatches = context?.userId
        ? !!verifiedContext?.userId && String(verifiedContext.userId) === String(context.userId)
        : true;
      const emailMatches = context?.email
        ? !!verifiedContext?.email && verifiedContext.email === context.email
        : true;
      const userMatchesFinal = !!verifiedContext?.userId && String(verifiedContext.userId) === String(context?.userId);
      const emailMatchesFinal = !!verifiedContext?.email && verifiedContext.email === context?.email;

      if (userMatches && emailMatches) {
        localStorage.removeItem(STORAGE_KEYS.EMAIL_VERIFIED_LOCALLY);
        return true;
      }
    } catch {
      localStorage.removeItem(STORAGE_KEYS.EMAIL_VERIFIED_LOCALLY);
    }

    return false;
  }
}
